import { useState } from 'react';
import * as R from 'ramda';

import DeleteModal from '@atom/components/common/DeleteModal';
import RenameModal from '@atom/components/common/RenameModal';
import renameIcon from '@atom/components/common/svgIcons/renameIcon.svg';
import { Icon, Menu } from '@atom/mui';
import colors from '@atom/styles/colors';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import textDisplayUtilities from '@atom/utilities/textDisplayUtilities';
import { setDisplayDate } from '@atom/utilities/timeUtilities';

import classes from './inventoryPhotos.module.css';

const MenuItem = Menu.MenuItem;

interface Props {
  media: any;
  renameMedia: (id: string, subjectId: string, name: string) => void;
  deleteMedia: (id: string) => void;
  mainMediaId: string;
  setAsPrimaryMedia: (mediaId: string) => void;
  loadingDelete: boolean;
  canMarkAsCoverPhoto: boolean;
  canUpdateMedia: boolean;
  canDeleteMedia: boolean;
}

const styles = {
  iconStyles: {
    color: colors.brand.blue,
    paddingRight: '0.5rem',
  },
};

const PhotoTile = ({
  media,
  renameMedia,
  deleteMedia,
  mainMediaId,
  setAsPrimaryMedia,
  loadingDelete,
  canMarkAsCoverPhoto,
  canUpdateMedia,
  canDeleteMedia,
}: Props) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [renameModalOpen, setRenameModalOpen] = useState<boolean>(false);

  const handleRename = (id: string, name: string) => {
    renameMedia(id, media.subjectId, name);
  };

  const handleDelete = async (id: string) => {
    await deleteMedia(id);
    setDeleteModalOpen(false);
  };

  const isMainMedia = mainMediaId === media.fileId;
  const coverPhotoText = isMainMedia
    ? 'Unmark as cover photo'
    : 'Mark as cover photo';
  const coverPhotoColor = isMainMedia ? colors.brand.blue : colors.neutral.gray;

  const imageStyle = {
    backgroundImage: `url('${FILES_ENDPOINT}/${media.fileId}_180')`,
  };

  return (
    <>
      <div className={classes.photoTile}>
        <div className={classes.tileImageContainer} style={imageStyle} />
        <div className={classes.imageInfoContainer}>
          <div className={classes.imageTitleContainer}>
            {isMainMedia ? (
              <Icon style={styles.iconStyles}>star</Icon>
            ) : (
              <div />
            )}
            <div
              className={classes.imageName}
            >{`${media.name}.${media.fileExtension}`}</div>
            <Menu noMinWidth IconButtonProps={{ size: 'small' }}>
              {canUpdateMedia && (
                <MenuItem
                  onClick={() => setRenameModalOpen(true)}
                  startAdornment={<img src={renameIcon} />}
                >
                  Rename
                </MenuItem>
              )}
              <a
                href={`${FILES_ENDPOINT}/${media.fileId}`}
                download={`${media.name}.${media.fileExtension}`}
              >
                <MenuItem startAdornment={<Icon>file_download</Icon>}>
                  Download
                </MenuItem>
              </a>
              {canMarkAsCoverPhoto && (
                <MenuItem
                  onClick={() => setAsPrimaryMedia(media.id)}
                  startAdornment={<Icon color={coverPhotoColor}>star</Icon>}
                >
                  {coverPhotoText}
                </MenuItem>
              )}
              {canDeleteMedia && (
                <MenuItem
                  onClick={() => setDeleteModalOpen(true)}
                  startAdornment={<Icon>delete</Icon>}
                >
                  Delete
                </MenuItem>
              )}
            </Menu>
          </div>
          <div className={classes.additionalInfoContainer}>
            <div>
              {!R.isEmpty(media.createdBy)
                ? textDisplayUtilities.displayName(media.createdBy)
                : ''}
            </div>
            <div>{`Uploaded on ${setDisplayDate(media.createdDate)}`}</div>
            <div>{`Captured on ${setDisplayDate(media.capturedDate)}`}</div>
          </div>
        </div>
      </div>
      <RenameModal
        id={media.id}
        name={media.name}
        type={media.type}
        renameAction={handleRename}
        open={renameModalOpen}
        closeModal={() => setRenameModalOpen(false)}
      />
      <DeleteModal
        open={deleteModalOpen}
        onConfirm={() => handleDelete(media.id)}
        onCancel={() => setDeleteModalOpen(false)}
        title={`Delete ${textDisplayUtilities.capitalize(media.type)}`}
        content={`Are you sure you want to delete this ${media.type}?`}
        loading={loadingDelete}
      />
    </>
  );
};

export default PhotoTile;
